import { Alert, Button, Result, Spin } from 'antd';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

export function getAuthRoles(user: { role: string; roles?: string[] } | null) {
  return new Set(user?.roles?.length ? user.roles : user?.role ? [user.role] : []);
}

function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <Spin size="large" />
    </div>
  );
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status, error, refresh } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingScreen />;
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (status === 'error') {
    return (
      <div style={{ maxWidth: 520, margin: '15vh auto' }}>
        <Alert
          type="error"
          showIcon
          message={error || '无法确认登录状态'}
          action={<Button onClick={() => void refresh()}>重试</Button>}
        />
      </div>
    );
  }
  return children;
}

export function PublicOnlyRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <LoadingScreen />;
  if (status === 'authenticated') return <Navigate to="/dashboard" replace />;
  return children;
}

export function RequireRole({
  roles,
  children,
}: {
  roles: string[];
  children: ReactNode;
}) {
  const { currentUser } = useAuth();
  if (!currentUser || !roles.some((role) => getAuthRoles(currentUser).has(role))) {
    return (
      <Result
        status="403"
        title="权限不足"
        subTitle="页面守卫只改善界面体验，服务端权限校验才是安全边界。"
      />
    );
  }
  return children;
}

export function RequirePoolDomain({
  domain,
  children,
}: {
  domain: 'creator' | 'merchant';
  children: ReactNode;
}) {
  const { currentUser } = useAuth();
  const roles = getAuthRoles(currentUser);
  const crossDomain = roles.has('bd') && (roles.has('creator') || roles.has('supervisor'));
  const allowed = roles.has('admin') || (!crossDomain && (
    domain === 'merchant'
      ? roles.has('bd')
      : roles.has('creator') || roles.has('supervisor')
  ));
  if (!currentUser || !allowed) {
    return (
      <Result
        status="403"
        title="权限不足"
        subTitle="商务与招商业务域相互隔离，服务端权限校验才是安全边界。"
      />
    );
  }
  return children;
}
